import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";
import { GOV_OPTIONS } from "../data/site";

const DETAIL = {
    "human-verification": {
        titleLines: ["Option 1 —", <>human <i className="text-amber">verification</i></>],
        lede: "The first option our research examines is the simplest to state: no action is ever taken on an algorithm's word alone. Every automated match must be confirmed by a trained human — with the authority to overrule it — before any stop, arrest or approach follows.",
        how: [
            "An automated match is treated as a lead, never as evidence. It identifies a candidate, not a suspect.",
            "A trained officer — not the operator who ran the query — reviews the match against independent evidence: the image quality, the context, the confidence score.",
            "Action requires positive human confirmation, recorded with the reviewer's identity, so every overrule is auditable later.",
            "Matches below the confidence threshold are deleted, not stored for future searching.",
        ],
        evidence:
            "This is not theoretical in New Zealand. In the Foodstuffs trial examined by the Privacy Commissioner, alerts required verification by two trained employees before anyone was approached, and the Commissioner demanded the confidence threshold rise from 90% to 92.5% after two misidentifications — human verification operating as the safety net that caught real errors (Office of the Privacy Commissioner, 2025).",
        strengths: [
            "Directly interrupts the false-positive harm our project centres on: the wrongful arrest in Detroit began with a match no human meaningfully tested (Hill, 2020).",
            "Preserves human judgement and discretion — the officer's experience still counts.",
            "Cheap and fast to implement inside existing police policy; it requires no new statute.",
        ],
        weaknesses: [
            "Automation bias: research and casework show humans tend to defer to machine output, especially under time pressure — 'confirmed by a human' can become 'rubber-stamped by a human'.",
            "Verification governs the match, not the scan. Everyone in the crowd is still identified, catalogued and compared — the chilling effect is untouched.",
            "Effectiveness depends on reviewer training and independence; an under-trained reviewer is a formality, not a safeguard.",
        ],
        verdict:
            "Our research finds human verification necessary but insufficient. It is the minimum condition — and the 2025 inquiry shows it working — but it leaves the front end of the system (who gets scanned, against what list) entirely inside police discretion.",
    },
    "independent-oversight": {
        titleLines: ["Option 2 —", <>independent <i className="text-amber">oversight</i></>],
        lede: "The second option attacks the structural problem: in New Zealand, police currently regulate police on FRT. Independent oversight would place biometric use under external, statutory rules — clear requirements around how biometric data is collected, stored, searched and deleted, with an external body empowered to check them.",
        how: [
            "A statutory framework defines when police FRT may be used, on what data, with what thresholds — set by Parliament, not by internal policy.",
            "An independent authority (or the Privacy Commissioner with expanded powers) approves each deployment in advance, informed by a mandatory privacy impact assessment and an equality/bias analysis.",
            "Accuracy is audited continuously against New Zealand demographics — directly answering the overseas-dataset problem the 2025 inquiry flagged (Office of the Privacy Commissioner, 2025).",
            "Transparency duties: a public register of deployments, published error rates, and OIA-ready documentation as the default.",
        ],
        evidence:
            "The alternative's cost is on the record. In Bridges v South Wales Police (2020), the Court of Appeal found the force's use of FRT unlawful largely because its own internal process had never adequately investigated bias or safeguards — exactly the failure an external reviewer exists to prevent. In New Zealand, the IPCA (2020) and the police's 2024 policy both concede the current model is self-regulation (Radio New Zealand, 2024); the OPC's draft Biometric Processing Privacy Code points the same direction — proportionality, transparency, mandatory safeguards (Office of the Privacy Commissioner, 2024).",
        strengths: [
            "Fixes what failed in 2018 and 2020: approval, scrutiny and bias analysis move outside the organisation that wants to deploy.",
            "Aligned with the direction New Zealand is already taking via the draft Biometric Code — it completes an existing reform rather than starting a new one.",
            "Durable: statutes and auditors outlast the internal policy memo that a future administration could delete.",
        ],
        weaknesses: [
            "Slower and costlier to establish than an internal rule; oversight bodies need funding, expertise and political will to have teeth.",
            "Oversight of a fast-moving technology can lag the technology itself — an audit regime needs its own technical capability.",
            "Rules alone do not stop scans; without verification and thresholds, an approved system can still misidentify.",
        ],
        verdict:
            "Our research finds independent oversight the option that addresses the root cause rather than the symptom — but it works best as a frame around Option 1, not instead of it.",
    },
    "temporary-restriction": {
        titleLines: ["Option 3 —", <>temporary <i className="text-amber">restriction</i></>],
        lede: "The third option is the precautionary one: a temporary restriction — a moratorium on police use of facial recognition in public spaces until the issues of bias and false positives are properly addressed and the law has caught up. Not a ban forever; a pause with a purpose.",
        how: [
            "A statutory moratorium suspends police FRT use in public spaces — live or retrospective — until defined conditions are met.",
            "The exit conditions are concrete: demographically fair accuracy demonstrated on New Zealand populations, an independent oversight regime in force, and biometric-specific legislation enacted.",
            "During the pause, police retain all existing lawful tools — witnesses, CCTV review by humans, existing databases — while the safeguards of Options 1 and 2 are built.",
            "The moratorium sunsets automatically if its conditions are met, preventing drift into a permanent ban without debate.",
        ],
        evidence:
            "The approach has real-world precedent. San Francisco banned agency use of facial recognition in 2019, explicitly on the false-positive and civil-rights record. The European Parliament has repeatedly called for a moratorium on police use of mass-surveillance biometrics in public spaces. And New Zealand's own Privacy Commissioner has shown what an evidence-first pause produces: his Foodstuffs inquiry insisted on hard conditions before calling FRT anything but an amber light (Office of the Privacy Commissioner, 2025).",
        strengths: [
            "The only option that guarantees no New Zealand Robert Williams until the accuracy and bias problems are demonstrably fixed.",
            "Creates genuine pressure to build Options 1 and 2 properly, rather than 'deploy now, safeguard later'.",
            "Honest about the current record: a legislature lagging the technology it is being deployed under.",
        ],
        weaknesses: [
            "Foregoes real benefits while the pause lasts — including missing-person and serious-crime uses that could be conducted under strict verification.",
            "Moratoria can harden into de facto permanent bans without public deliberation — or be quietly lifted in the other direction.",
            "Enforcement is awkward: a restriction polices the police, which presumes exactly the independent monitoring that Option 2 would build.",
        ],
        verdict:
            "Our research finds temporary restriction the strongest protection and the highest cost. It is defensible as a starting position — pause, build oversight, then deploy on proof — precisely because the other two options remain available at the end of it.",
    },
};

export default function GovernanceOption() {
    const { slug } = useParams();
    const option = GOV_OPTIONS.find((o) => o.slug === slug);
    const detail = DETAIL[slug];
    if (!option || !detail) return <Navigate to="/governance-options" replace />;

    const idx = GOV_OPTIONS.indexOf(option);
    const next = GOV_OPTIONS[(idx + 1) % GOV_OPTIONS.length];

    return (
        <div>
            <PageHero
                fileNo={`FR-NZ-${option.no}`}
                lines={detail.titleLines}
                lede={detail.lede}
                meta={["SUBJECT: GOVERNANCE ANALYSIS"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead no="a" kicker="Mechanism" title="How it would work" />
                    <div className="space-y-px bg-white/10 border border-white/10">
                        {detail.how.map((h, i) => (
                            <Reveal key={i} delay={i * 0.05} className="bg-ink">
                                <div className="flex gap-6 p-6 sm:p-8" data-testid={`option-how-${slug}-${i + 1}`}>
                                    <span className="font-display text-stroke-amber text-3xl font-semibold leading-none w-12 shrink-0">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <p className="text-sm sm:text-base leading-relaxed text-ash pt-1">{h}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead no="b" kicker="Evidence base" title="What the record shows" />
                    <Reveal>
                        <blockquote className="border-l-2 border-amber pl-6 sm:pl-10 max-w-4xl">
                            <p className="font-display italic text-xl sm:text-2xl text-paper leading-snug">{detail.evidence}</p>
                        </blockquote>
                    </Reveal>
                    <div className="grid lg:grid-cols-2 gap-8 mt-14">
                        <Reveal>
                            <div className="border border-match/30 bg-match/[0.04] p-7 sm:p-8 h-full" data-testid={`option-strengths-${slug}`}>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-match mb-6">The case for</p>
                                <ul className="space-y-4">
                                    {detail.strengths.map((s, i) => (
                                        <li key={i} className="flex gap-3 text-sm leading-relaxed text-ash">
                                            <CheckCircle2 size={16} className="text-match shrink-0 mt-0.5" />
                                            {s}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="border border-alert/30 bg-alert/[0.04] p-7 sm:p-8 h-full" data-testid={`option-weaknesses-${slug}`}>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-alert mb-6">The case against</p>
                                <ul className="space-y-4">
                                    {detail.weaknesses.map((w, i) => (
                                        <li key={i} className="flex gap-3 text-sm leading-relaxed text-ash">
                                            <XCircle size={16} className="text-alert shrink-0 mt-0.5" />
                                            {w}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
                    <Reveal>
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber mb-5">Our finding</p>
                        <p className="font-display text-2xl sm:text-4xl text-paper leading-snug max-w-4xl">{detail.verdict}</p>
                    </Reveal>
                    <Reveal delay={0.12} className="mt-12">
                        <a
                            href={`/governance-options/${next.slug}`}
                            onClick={(e) => {
                                e.preventDefault();
                                window.__lenis?.scrollTo(0, { immediate: true });
                                window.history.pushState({}, "", `/governance-options/${next.slug}`);
                                window.dispatchEvent(new PopStateEvent("popstate"));
                            }}
                            data-testid={`option-next-link-${next.slug}`}
                            className="group inline-flex items-center gap-4 border border-white/15 px-6 py-4 hover:border-amber transition-colors"
                        >
                            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ash group-hover:text-amber">
                                Next option — {next.title}
                            </span>
                            <ArrowRight size={15} className="text-amber" />
                        </a>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
